import { useEffect, useRef, useState } from "react";
import { Check, Trash2, Upload, FolderInput, Gift as GiftIcon, RefreshCw, Languages, Plus, GripVertical } from "lucide-react";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../../components/ui/alert-dialog";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import api, { formatApiError, imageUrl } from "../../lib/api";

export default function Gifts() {
  const [gifts, setGifts] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0 });
  const [tab, setTab] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [syncing, setSyncing] = useState("");
  const [uploading, setUploading] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [dragIndex, setDragIndex] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [newFile, setNewFile] = useState(null);
  const [newName, setNewName] = useState("");
  const [newNameTr, setNewNameTr] = useState("");
  const [newPrice, setNewPrice] = useState("");
  const [adding, setAdding] = useState(false);
  const fileRef = useRef(null);
  const addFileRef = useRef(null);

  const fetchGifts = () => {
    api.get("/admin/gifts", { params: { status: tab } })
      .then((r) => { setGifts(r.data.gifts); setCounts(r.data.counts); })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { setLoading(true); fetchGifts(); }, [tab]);

  const importDataset = async () => {
    if (importing) return;
    setImporting(true);
    try {
      const res = await api.post("/admin/gifts/import");
      toast.success(res.data.message);
      fetchGifts();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setImporting(false);
    }
  };

  const syncCatalog = async (kind) => {
    if (syncing) return;
    setSyncing(kind);
    try {
      const res = await api.post("/admin/gifts/scrape-streamtoearn", null, { timeout: 30000 });
      toast.success(res.data.message);
      let done = false;
      for (let i = 0; i < 80 && !done; i++) {
        await new Promise((r) => setTimeout(r, 5000));
        try {
          const st = await api.get("/admin/gifts/scrape-status");
          done = !st.data.running;
          if (done && st.data.last_result) {
            if (st.data.last_result.error) toast.error(st.data.last_result.error);
            else toast.success(st.data.last_result.message || "İçe aktarma tamamlandı");
          }
        } catch (e) { /* durum sorgusu atla */ }
      }
      fetchGifts();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSyncing("");
    }
  };

  const translateTr = async () => {
    if (syncing) return;
    setSyncing("translate");
    try {
      const res = await api.post("/admin/gifts/translate-tr");
      toast.success(res.data.message);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSyncing("");
    }
  };

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/admin/gifts/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Görsel yüklendi (onay bekliyor)");
      fetchGifts();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const approve = async (gift) => {
    try {
      await api.patch(`/admin/gifts/${gift.id}`, { status: "approved" });
      toast.success("Görsel onaylandı");
      setGifts((prev) => prev.filter((g) => g.id !== gift.id));
      setCounts((c) => ({ pending: c.pending - 1, approved: c.approved + 1 }));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/admin/gifts/${deleteId}`);
      toast.success("Görsel silindi");
      const wasPending = tab === "pending";
      setGifts((prev) => prev.filter((g) => g.id !== deleteId));
      setCounts((c) => ({ ...c, [wasPending ? "pending" : "approved"]: c[wasPending ? "pending" : "approved"] - 1 }));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeleteId(null);
    }
  };

  const onDropGift = async (targetIndex) => {
    if (dragIndex === null || dragIndex === targetIndex) {
      setDragIndex(null);
      return;
    }
    const reordered = [...gifts];
    const [moved] = reordered.splice(dragIndex, 1);
    reordered.splice(targetIndex, 0, moved);
    setGifts(reordered);
    setDragIndex(null);
    try {
      await api.post("/admin/gifts/reorder", { ids: reordered.map((g) => g.id) });
      toast.success("Sıralama güncellendi");
    } catch (e) {
      toast.error(formatApiError(e));
      fetchGifts();
    }
  };

  const addGift = async () => {
    if (adding) return;
    if (!newName.trim()) {
      toast.error("İngilizce hediye adı gerekli");
      return;
    }
    if (!newFile) {
      toast.error("Görsel seçmelisiniz");
      return;
    }
    setAdding(true);
    try {
      const fd = new FormData();
      fd.append("file", newFile);
      fd.append("name", newName.trim());
      fd.append("name_tr", newNameTr.trim());
      fd.append("price", newPrice || "0");
      const res = await api.post("/admin/gifts/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(res.data.message || "Hediye eklendi");
      setShowAdd(false);
      setNewFile(null);
      setNewName("");
      setNewNameTr("");
      setNewPrice("");
      fetchGifts();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAdding(false);
    }
  };

  const tabCls = (t) =>
    `text-xs font-semibold rounded-full px-4 py-2 transition-colors border ${
      tab === t ? "text-white bg-gradient-to-r from-[#0047FF]/30 to-[#00F0FF]/20 border-cyan-400/40" : "text-[#A0ABC0] border-white/10 hover:border-cyan-400/30"
    }`;

  return (
    <div data-testid="admin-gifts-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <h1 className="font-display font-700 text-xl text-white">Hediye Görselleri</h1>
        <div className="flex flex-wrap items-center gap-2.5">
          <button onClick={() => setShowAdd(true)} data-testid="gifts-add-button"
            className="btn-glow inline-flex items-center gap-2 text-xs font-semibold text-white px-4 py-2.5 rounded-full">
            <Plus size={15} /> Yeni Hediye Ekle
          </button>
          <button onClick={() => syncCatalog("ste")} disabled={!!syncing} data-testid="gifts-sync-ste-button"
            className="btn-glow inline-flex items-center gap-2 text-xs font-semibold text-white px-4 py-2.5 rounded-full disabled:opacity-50">
            <RefreshCw size={15} />
            {syncing === "ste" ? "Güncelleniyor..." : "StreamToEarn'den Güncelle"}
          </button>
          <button onClick={translateTr} disabled={!!syncing} data-testid="gifts-translate-button"
            className="inline-flex items-center gap-2 text-xs font-semibold text-white border border-white/15 bg-black/40 hover:border-cyan-400/50 transition-colors rounded-full px-4 py-2.5 disabled:opacity-50">
            <Languages size={15} />
            {syncing === "translate" ? "Başlatılıyor..." : "Türkçe Çeviri"}
          </button>
          <button onClick={importDataset} disabled={importing} data-testid="gifts-import-button"
            className="inline-flex items-center gap-2 text-xs font-semibold text-white border border-white/15 bg-black/40 hover:border-cyan-400/50 transition-colors rounded-full px-4 py-2.5 disabled:opacity-50">
            <FolderInput size={15} />
            {importing ? "İçe Aktarılıyor..." : "Dataset'ten İçe Aktar"}
          </button>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files[0])} data-testid="gifts-upload-input" />
          <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="gifts-upload-button"
            className="inline-flex items-center gap-2 text-xs font-semibold text-white border border-white/15 bg-black/40 hover:border-cyan-400/50 transition-colors rounded-full px-4 py-2.5 disabled:opacity-50">
            <Upload size={15} />
            {uploading ? "Yükleniyor..." : "Görsel Yükle"}
          </button>
        </div>
      </div>
      <p className="text-sm text-[#64748B] mb-6">Onaylanan sekmesinde sürükleyerek hediye sıralamasını değiştirebilirsiniz — sıralama üye paneline doğrudan yansır.</p>

      <div className="flex gap-2.5 mb-6" data-testid="gifts-tabs">
        <button onClick={() => setTab("pending")} data-testid="gifts-tab-pending" className={tabCls("pending")}>
          Onay Bekleyen ({counts.pending})
        </button>
        <button onClick={() => setTab("approved")} data-testid="gifts-tab-approved" className={tabCls("approved")}>
          Onaylanan ({counts.approved})
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-[#64748B]">Yükleniyor...</p>
      ) : gifts.length === 0 ? (
        <div data-testid="gifts-empty" className="rounded-2xl border border-dashed border-white/10 bg-[#0A0E17]/60 py-16 text-center">
          <GiftIcon size={32} strokeWidth={1.5} className="text-[#64748B] mx-auto mb-4" />
          <p className="text-[#A0ABC0] text-sm">
            {tab === "pending" ? "Onay bekleyen görsel yok. Dataset'ten içe aktarın veya görsel yükleyin." : "Henüz onaylanmış görsel yok."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-testid="gifts-grid">
          {gifts.map((g, index) => (
            <div key={g.id} data-testid={`gift-card-${g.id}`}
              draggable={tab === "approved"}
              onDragStart={() => tab === "approved" && setDragIndex(index)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => tab === "approved" && onDropGift(index)}
              onDragEnd={() => setDragIndex(null)}
              className={`card-lift rounded-2xl border bg-[#0A0E17] overflow-hidden transition-colors ${
                dragIndex === index ? "opacity-50 border-cyan-400/40" : "border-white/[0.07]"
              } ${tab === "approved" ? "cursor-grab active:cursor-grabbing" : ""}`}>
              <div className="aspect-square bg-[#0F1626] flex items-center justify-center p-3 relative">
                {tab === "approved" && (
                  <GripVertical size={14} className="text-[#64748B] absolute top-2 left-2" data-testid={`gift-drag-${g.id}`} />
                )}
                <img src={imageUrl(g.image)} alt={g.name || "Hediye görseli"} className="max-w-full max-h-full object-contain" loading="lazy" />
              </div>
              <div className="p-3">
                <div className="flex items-center justify-between gap-2 mb-0.5">
                  <span className="text-xs font-semibold text-white truncate">{g.name || "İsimsiz"}</span>
                  <span className="text-[10px] font-bold text-cyan-300 shrink-0" data-testid={`gift-price-${g.id}`}>{g.price ?? 0} Jeton</span>
                </div>
                <p className="text-[10px] text-[#64748B] truncate mb-3">{g.name_tr || (g.source === "upload" ? "Yükleme" : "Scraper")}</p>
                <div className="flex gap-2">
                  {g.status === "pending" && (
                    <button onClick={() => approve(g)} data-testid={`gift-approve-${g.id}`}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 text-[11px] font-semibold text-emerald-300 border border-emerald-400/30 rounded-lg py-2 hover:bg-emerald-400/10 transition-colors">
                      <Check size={13} /> Onayla
                    </button>
                  )}
                  <button onClick={() => setDeleteId(g.id)} data-testid={`gift-delete-${g.id}`}
                    className={`${g.status === "pending" ? "" : "flex-1"} inline-flex items-center justify-center gap-1.5 text-[11px] font-semibold text-red-300 border border-red-400/30 rounded-lg py-2 px-3 hover:bg-red-400/10 transition-colors`}>
                    <Trash2 size={13} /> Sil
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="bg-[#0A0E17] border-white/10 text-white max-w-md" data-testid="gift-add-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-base">Yeni Hediye Ekle</DialogTitle>
            <DialogDescription className="text-xs text-[#64748B]">İsim ve jeton bilgisiyle hediye ekleyin. Aynı isimli hediye varsa bilgileri ve görseli güncellenir.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <button type="button" onClick={() => addFileRef.current?.click()} data-testid="gift-add-image-button"
                className="w-20 h-20 rounded-xl border border-dashed border-white/15 bg-black/40 flex items-center justify-center overflow-hidden hover:border-cyan-400/50 transition-colors shrink-0">
                {newFile ? <img src={URL.createObjectURL(newFile)} alt="Önizleme" className="max-w-full max-h-full object-contain" /> : <Upload size={18} className="text-[#64748B]" />}
              </button>
              <input ref={addFileRef} type="file" accept="image/*" className="hidden" onChange={(e) => setNewFile(e.target.files[0])} data-testid="gift-add-image-input" />
              <p className="text-[11px] text-[#64748B]">PNG, WebP, JPG veya GIF (maks 5MB). Transparan PNG önerilir.</p>
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-[#A0ABC0] mb-1.5">İngilizce Adı <span className="text-cyan-400">*</span></label>
              <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Örn: Heart" data-testid="gift-add-name"
                className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-[#64748B] outline-none focus:border-cyan-400/60 transition-colors" />
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-[#A0ABC0] mb-1.5">Türkçe Adı</label>
              <input value={newNameTr} onChange={(e) => setNewNameTr(e.target.value)} placeholder="Örn: Kalp" data-testid="gift-add-name-tr"
                className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-[#64748B] outline-none focus:border-cyan-400/60 transition-colors" />
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-[#A0ABC0] mb-1.5">Jeton Fiyatı</label>
              <input type="number" min="0" value={newPrice} onChange={(e) => setNewPrice(e.target.value)} placeholder="Örn: 10" data-testid="gift-add-price"
                className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-[#64748B] outline-none focus:border-cyan-400/60 transition-colors" />
            </div>
            <button onClick={addGift} disabled={adding} data-testid="gift-add-submit"
              className="btn-glow w-full inline-flex items-center justify-center gap-2 text-xs font-semibold text-white py-3 rounded-full disabled:opacity-50">
              <Plus size={14} /> {adding ? "Ekleniyor..." : "Hediyeyi Ekle"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="bg-[#0A0E17] border-white/10 text-white" data-testid="gift-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Görseli Sil</AlertDialogTitle>
            <AlertDialogDescription className="text-[#A0ABC0]">Bu görseli silmek istediğinize emin misiniz? Üyelerin kayıtlarından da kaldırılır.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="gift-delete-cancel" className="bg-transparent border-white/10 text-white hover:bg-white/5">Vazgeç</AlertDialogCancel>
            <AlertDialogAction data-testid="gift-delete-confirm" onClick={remove} className="bg-red-500 hover:bg-red-600 text-white">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
